import { useEffect, useId, useMemo, useRef, useState } from "react"
import { ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils"

export type SearchableSelectOption = {
  value: string
  label: string
  /** Extra text used for filtering (e.g. account ID). */
  searchText?: string
  disabled?: boolean
}

type SearchableSelectProps = {
  value: string
  options: SearchableSelectOption[]
  onChange: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  disabled?: boolean
  className?: string
  "aria-label"?: string
}

/**
 * Single-select combobox: type in the field to filter options by label / searchText.
 */
export function SearchableSelect({
  value,
  options,
  onChange,
  placeholder = "Select an option",
  searchPlaceholder = "Search...",
  disabled = false,
  className,
  "aria-label": ariaLabel,
}: SearchableSelectProps) {
  const listId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")

  const selected = options.find((o) => o.value === value)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => {
      const haystack = `${o.label} ${o.searchText ?? ""} ${o.value}`.toLowerCase()
      return haystack.includes(q)
    })
  }, [options, query])

  useEffect(() => {
    if (!open) return
    const onDocMouseDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false)
        setQuery("")
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false)
        setQuery("")
      }
    }
    document.addEventListener("mousedown", onDocMouseDown)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown)
      document.removeEventListener("keydown", onKey)
    }
  }, [open])

  const openMenu = () => {
    if (disabled) return
    setOpen(true)
    setQuery("")
    queueMicrotask(() => inputRef.current?.focus())
  }

  const pick = (next: string) => {
    onChange(next)
    setOpen(false)
    setQuery("")
  }

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <div
        className={cn(
          "flex h-10 overflow-hidden rounded-md border border-[#555] bg-[#3a3a3a]",
          open && "border-[#8b9aff] ring-1 ring-[#8b9aff]/40",
          disabled && "opacity-60",
        )}
      >
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-label={ariaLabel}
          disabled={disabled}
          value={open ? query : (selected?.label ?? "")}
          placeholder={selected ? undefined : placeholder}
          onFocus={openMenu}
          onClick={openMenu}
          onChange={(e) => {
            setQuery(e.target.value)
            if (!open) setOpen(true)
          }}
          className="min-w-0 flex-1 bg-transparent px-3 text-sm text-white placeholder:text-[#888] focus:outline-none"
        />
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          aria-label="Toggle options"
          onClick={() => {
            if (open) {
              setOpen(false)
              setQuery("")
            } else {
              openMenu()
            }
          }}
          className="flex w-10 shrink-0 items-center justify-center border-l border-[#555] bg-[#667eea] text-white hover:bg-[#5568d3] disabled:cursor-not-allowed"
        >
          <ChevronDown
            className={cn("h-4 w-4 transition-transform", open && "rotate-180")}
          />
        </button>
      </div>

      {open ? (
        <div
          id={listId}
          role="listbox"
          className="absolute left-0 right-0 z-50 mt-1 max-h-56 overflow-y-auto rounded-md border border-[#555] bg-[#2d2d2d] py-1 shadow-lg"
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-[#888]">No matches</div>
          ) : (
            filtered.map((opt) => {
              const isSelected = opt.value === value
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  disabled={opt.disabled}
                  title={opt.label}
                  onClick={() => {
                    if (!opt.disabled) pick(opt.value)
                  }}
                  className={cn(
                    "block w-full px-3 py-2 text-left text-sm text-[#e0e0e0] hover:bg-[#3a3a3a]",
                    isSelected && "bg-[#667eea]/20 text-[#8b9aff]",
                    opt.disabled &&
                      "cursor-not-allowed text-[#666] hover:bg-transparent",
                  )}
                >
                  <span className="whitespace-normal break-words">{opt.label}</span>
                </button>
              )
            })
          )}
        </div>
      ) : null}

      {!open && !selected ? (
        <span className="sr-only">{searchPlaceholder}</span>
      ) : null}
    </div>
  )
}
